import { DropdownProps } from "@fluentui/react-northstar";
import { useTranslate } from "front";
import { useGroupsCache } from "../../hooks/useQuery/useGroupsCache";
import { IContact } from "../../interfaces/IContact/IContact";
import { useEffect, useMemo, useState } from "react";
import { Control, UseFormWatch } from "react-hook-form";
import { translations } from "../../translations";
import { parseGroupIds } from "../../utils/parseGroupIds";

export const useGroupTabs = (props: { canModify: boolean; contact: IContact | undefined; control: Control<IContact, any>; watch: UseFormWatch<any> }) => {
  const t = useTranslate(translations);
  const { queryGroups } = useGroupsCache();
  const [dropdownValue, setDropdownValue] = useState<any>([]);
  const [canShowNoResult, setCanShowNoResult] = useState(false);

  useEffect(() => {
    if (props.canModify) return;
    setDropdownValue([]);
  }, [props.canModify]);

  const rawGroupId = props.watch("groupId");
  const groupIdPatch = { add: rawGroupId?.add ?? [], remove: rawGroupId?.remove ?? [] };

  /** Matches submit semantics in ContactDialog: existing groups minus removals, plus adds (avoids flaky string `.includes` on groupId). */
  const selectedGroupIds = useMemo(() => {
    const initial =
      props.contact?.groupId
        ? parseGroupIds(props.contact.groupId)
        : [];
    const rest = initial.filter((id) => !groupIdPatch.remove.includes(id));
    return [...new Set([...rest, ...groupIdPatch.add])];
  }, [props.contact?.groupId, groupIdPatch.add, groupIdPatch.remove]);

  const getAllGroups = useMemo(() => {
    const allGroups = queryGroups.data ?? [];
    return selectedGroupIds.map((id) => allGroups.find((g) => g.id === id)).filter((g): g is NonNullable<typeof g> => g != null);
  }, [queryGroups.data, selectedGroupIds]);

  const handleDropdown = (_: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null, data: DropdownProps, field: any) => {
    const ids = (data.value as any).map((g: any) => {
      if (typeof g === "string") {
        const selectedGroup = queryGroups.data.find((i) => i.name === g);
        return selectedGroup?.id;
      }
      return g.id;
    });
    field.value.add = ids;
    field.onChange(field.value);
  };

  const handleRemove = (id: string, field: any) => {
    field.value.remove.push(id);
    field.onChange(field.value);
  };

  const onSearchQueryChange = (_: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null, data: DropdownProps) => {
    const value = data.searchQuery as string;
    const result = queryGroups.data.find((g) => g.name.toLowerCase().includes(value.toLocaleLowerCase()));
    setCanShowNoResult(!result);
  };

  return { t, allGroups: queryGroups.data, handleDropdown, getAllGroups, handleRemove, dropdownValue, setDropdownValue, onSearchQueryChange, canShowNoResult };
};
